import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { t } from 'ttag';

import client from 'utils/client';

import { Button, Input, Modal } from 'components';

import {
  RiCheckboxBlankLine,
  RiCheckboxFill,
  RiLoginBoxFill
} from 'react-icons/ri';

function Register({
  isOpen,
  onClose,
  authConfig,
  handleEmailRegistration,
  errorMessage
}) {
  /**
   * The Validation Schema for the Formik Form.
   * @type {Yup.ObjectSchema}
   */
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        email: Yup.string()
          .email(t`The email is not valid.`)
          .required(t`An email is required.`),
        consent: Yup.boolean().oneOf(
          [true],
          t`You need to read and accept the Terms and Conditions and Privacy Policy.`
        )
      }),
    []
  );

  const [permissionText, setPermissionText] = useState('');

  useEffect(() => {
    const fetchPermissionText = async () => {
      const { data } = await client.get(
        '/policies/membership_short_permission/'
      );
      setPermissionText(data.permission_text);
    };

    fetchPermissionText();
  }, []);

  /**
   * A function that renders the Terms and Conditions link.
   * @type {node}
   */
  const termsAndConditions = useMemo(
    () => (
      <a
        href="/policies/membership_long_permission/"
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-green underline"
        tabIndex={-1}
      >
        {t`Terms and Conditions`}
      </a>
    ),
    []
  );

  /**
   * A function that renders the Privacy Policy link.
   * @type {node}
   */
  const privacyPolicy = useMemo(
    () => (
      <a
        href="/policies/privacy/"
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-green underline"
        tabIndex={-1}
      >
        {t`Privacy Policy`}
      </a>
    ),
    []
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => onClose()}
      title={t`Get ready to join Unisport`}
      maxWidth="md"
    >
      <p className="m-0 text-sm text-dark-gray">
        {t`It looks like you don't have a Unisport account yet. Do not worry, we will create one for you in no time! You just need to confirm your email address and accept the terms and conditions.`}
      </p>
      <Formik
        initialValues={{
          email: authConfig.email || '',
          consent: false
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          handleEmailRegistration(
            values,
            '/auth/api/complete/email/',
            setSubmitting
          );
        }}
      >
        {({ isSubmitting }) => (
          <Form className="pt-4">
            <Field name="email">
              {({ field, meta }) => (
                <Input
                  {...field}
                  id="login-email"
                  label="Email"
                  type="email"
                  isRequired
                  isInvalid={meta.touched && meta.error}
                  errorMessage={meta.error}
                  isValid={meta.touched && !meta.error}
                  disabled={isSubmitting}
                />
              )}
            </Field>
            <Field name="consent">
              {({ field, meta }) => (
                <label
                  htmlFor="consent"
                  className="mb-8 flex cursor-pointer items-start focus:outline-none focus-visible:ring-2 focus-visible:ring-deep-blue focus-visible:ring-offset-2"
                >
                  <input
                    {...field}
                    id="consent"
                    className="peer hidden"
                    aria-label={t`Accept Terms of Service`}
                    type="checkbox"
                    disabled={isSubmitting}
                  />
                  <RiCheckboxBlankLine className="mr-2 block h-6 w-6 shrink-0 text-gray disabled:opacity-50 peer-checked:hidden" />
                  <RiCheckboxFill className="mr-2 hidden h-6 w-6 shrink-0 text-green disabled:opacity-50 peer-checked:block" />
                  <div>
                    <p className="mt-0.5 text-sm text-dark-gray">
                      {permissionText}
                      <br />
                      {termsAndConditions} | {privacyPolicy}
                    </p>
                    {meta.touched && meta.error && (
                      <p className="m-0 text-sm text-cinnebar">{meta.error}</p>
                    )}
                  </div>
                </label>
              )}
            </Field>
            <Button
              variant="primary"
              alignment="left"
              type="submit"
              icon={<RiLoginBoxFill className="h-4 w-4 shrink-0" />}
              isLoading={isSubmitting}
              isFullWidth
            >
              {t`Continue`}
            </Button>
          </Form>
        )}
      </Formik>
      {errorMessage !== '' && (
        <p className="m-0 text-sm text-cinnebar">{errorMessage}</p>
      )}
    </Modal>
  );
}

Register.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  authConfig: PropTypes.shape({
    email: PropTypes.string,
    backend: PropTypes.string,
    partialToken: PropTypes.string
  }).isRequired,
  handleEmailRegistration: PropTypes.func.isRequired,
  errorMessage: PropTypes.string
};

Register.defaultProps = {
  isOpen: false,
  onClose: () => {},
  errorMessage: ''
};

export default Register;
